import { call, delay, put, select, spawn, takeLatest } from 'redux-saga/effects';

import { clearOnlinePresence, logoutRequest } from '../../services/api';
import {
	clearPendingInviteCode,
	getPendingInviteCode,
} from '../../services/invite/pendingInviteCode';
import { closeSocket } from '../../services/socket';
import API from '../../utils/API';
import { env } from '../../utils/env';
import { flattenApiMessage, getApiErrorMessage } from '../../utils/helpers/apiErrorMessage';
import { clearAuthFromStorage, loadStoredUser } from '../../utils/helpers/authStorage';
import { isTokenExpired } from '../../utils/helpers/tokenUtils';
import LS from '../../utils/localStorageUtils';
import { UserProfile, UserStatistics } from '../types';
import {
	appleLoginFailure,
	appleLoginRequest,
	BanNotice,
	checkAuthFailure,
	checkAuthRequest,
	checkAuthSuccess,
	facebookLoginFailure,
	facebookLoginRequest,
	googleLoginFailure,
	googleLoginRequest,
	loginFailure,
	loginRequest,
	loginSuccess,
	logout,
	profileFailure,
	profileRequest,
	profileSuccess,
	registerFailure,
	registerRequest,
	registerSuccess,
	rehydrateAuth,
	reportUserFailure,
	reportUserRequest,
	reportUserSuccess,
	setBanNotice,
	setLoginPendingConfirmation,
	updateBonusCooldown,
	updateProfileFailure,
	updateProfileRequest,
	updateProfileSuccess,
	userStatsFailure,
	userStatsRequest,
	userStatsSuccess,
} from './authActions';

interface RawProfileData {
	id: number;
	stringId: string;
	email: string;
	username: string;
	sound_effects: number;
	avatar?: string;
	wallet: { chips: number };
	is_vip_user: boolean;
	level: number;
	user_purchases: { [key: string]: boolean };
	email_notifications: number;
	mobile_notifications: number;
	hand_power_meter: number;
	periodic_bonus_collected_at: string;
	periodic_bonus_hours: string;
	vip_expired_at: string;
	gender?: 'Male' | 'Female' | null;
	age?: number | null;
	location?: string | null;
	description?: string | null;
	username_changes_left?: number | null;
	avatar_changes_left?: number | null;
	gamesData?: { svarka_minimal_experience?: number; svarka_maximal_experience?: number };
}

function getAuthErrorMessage(errorData: any, fallback: string) {
	return flattenApiMessage(errorData?.message) || fallback;
}

const BAN_ERROR_CODES = new Set([
	'T_AUTH_LOGIN_ACCOUNT_BANNED',
	'T_AUTH_LOGIN_ACCOUNT_BANNED_UNTIL',
	'T_AUTH_LOGIN_IP_BANNED',
	'T_AUTH_LOGIN_DEVICE_BANNED',
]);

/**
 * gateway-game answers a banned login/registration with 401 `{ code, message, ban: { type,
 * expiresAt, reason } }`. Those are rendered by BannedModal instead of the generic toast.
 */
function toBanNotice(errorData: any, fallback: string): BanNotice | null {
	const code = String(errorData?.code || '');

	if (!BAN_ERROR_CODES.has(code)) {
		return null;
	}

	return {
		code,
		message: getAuthErrorMessage(errorData, fallback),
		type: errorData?.ban?.type,
		expiresAt: errorData?.ban?.expiresAt ?? null,
		reason: errorData?.ban?.reason,
	};
}

import {
	createPlayerReport,
	getUserProfile,
	getUserStatistics,
	refreshTokenRequest,
	socialLoginApple,
	socialLoginFacebook,
	socialLoginGoogle,
	updateProfile,
	UpdateProfileResult,
} from '../../services/api';
import { cleverTapService } from '../../services/clevertap/clevertap.service';
import { deltaDNAService } from '../../services/deltaDNA/deltadna.service';
import { analyticsEvents } from '../../services/firebase/analytics-events.service';
import { firebaseMessaging } from '../../services/firebase/firebase.messaging';
import { appleSignIn } from '../../services/socialLogins/appleSignIn';
import { facebookSignIn } from '../../services/socialLogins/facebookSignIn';
import googleSignIn from '../../services/socialLogins/googleSignIn';
import { toast } from '../../utils/helpers/toastUtils';
import { RootState } from '..';
import { miniGamesRewardsRequest } from '../jackpot/miniGamesRewardsActions';

function normalizeUserPurchases(
	raw: Record<string, boolean> | undefined,
): UserProfile['user_purchases'] {
	if (!raw || typeof raw !== 'object') return {};
	return {
		...raw,
		london: !!(raw.london ?? raw.is_second_room_bought),
		paris: !!(raw.paris ?? raw.is_third_room_bought),
		monaco: !!(raw.monaco ?? raw.is_forth_room_bought),
		macao: !!(raw.macao ?? raw.is_fifth_room_bought),
		vegas: !!(raw.vegas ?? raw.is_sixth_room_bought),
	};
}

function mapProfile(player: RawProfileData): UserProfile {
	const vipExpiredAt = (player as any).vip_expired_at ?? (player as any).vipExpiredAt ?? null;
	return {
		id: player.stringId,
		numericId: player.id,
		username: player.username,
		email: player.email,
		isSoundEffectsEnabled: !!player.sound_effects,
		avatarFileId: player.avatar || '',
		chips: player.wallet.chips || 0,
		isVip: player.is_vip_user,
		level: player.level,
		user_purchases: normalizeUserPurchases(player.user_purchases),

		isEmailEnabled: !!player.email_notifications,
		isMobileEnabled: !!player.mobile_notifications,
		isHandPowerEnabled: !!player.hand_power_meter,
		collectedAt: player.periodic_bonus_collected_at,
		bonusHours: player.periodic_bonus_hours,
		vipExpiredAt,
		gender: player.gender ?? null,
		age: player.age ?? null,
		location: player.location ?? null,
		description: player.description ?? null,
		usernameChangesLeft: player.username_changes_left,
		avatarChangesLeft: player.avatar_changes_left,
		// Despite the names, the backend sends the XP earned inside the current level
		// (`currentXp`) and the XP that level takes to complete (`xpToNext`).
		levelXp: Number(player.gamesData?.svarka_minimal_experience ?? 0),
		levelXpToNext: Number(player.gamesData?.svarka_maximal_experience ?? 0),
	};
}

function* loginSaga(action: ReturnType<typeof loginRequest>) {
	try {
		const payload = {
			username: action.payload.username,
			password: action.payload.password,
			...(action.payload.confirmed !== undefined ? { confirmed: action.payload.confirmed } : {}),
		};

		const response = yield call([API, API.authRequest], '/login', 'post', payload, {
			'Content-Type': 'application/json',
			...API.getXCasinoHeader(),
		});

		if (response.Error) throw response.Error;

		const {
			access_token,
			refresh_token,
			user,
		}: { access_token: string; refresh_token?: string; user: any } = response.Data;

		LS.set('access_token', access_token);
		if (refresh_token) {
			LS.set('refresh_token', refresh_token);
		}

		yield put(loginSuccess({ token: access_token, user }));
		toast.success('auth.login_success_title', 'Login successful', 'Welcome back!');
	} catch (err: any) {
		const errorData = err?.response?.data || {};
		const code = errorData?.code || '';
		const message = getAuthErrorMessage(errorData, err?.message || 'Login failed');

		const ban = toBanNotice(errorData, message);
		if (ban) {
			yield put(loginFailure(errorData));
			yield put(setBanNotice(ban));
			return;
		}

		if (code === 'T_AUTH_LOGIN_ACTIVE_SESSION_CONFIRM_REQUIRED') {
			yield put(
				setLoginPendingConfirmation({
					type: 'password',
					username: action.payload.username,
					password: action.payload.password,
				}),
			);
			return;
		}

		yield put(loginFailure(errorData));
		toast.errorText(message);
	}
}

function* registerWorker(action: ReturnType<typeof registerRequest>) {
	try {
		const casinoId = env('CASINO_ID') as string;
		const email = action.payload.email?.trim().toLowerCase();
		const password = action.payload.password ?? '';
		const emailLeftPart = email?.split('@')[0]?.trim();
		const username = emailLeftPart || action.payload.username?.trim() || email;
		const dobFromPayload = action.payload.dateOfBirth?.trim();
		const dateOfBirth = dobFromPayload || '1999-01-01T12:00:00.000Z';
		const registrationBodyObject: Record<string, string | boolean> = {
			casinoId,
			email,
			password,
			username,
			firstName: action.payload.firstName ?? '',
			lastName: action.payload.lastName ?? '',
			mobileNumber: action.payload.mobileNumber ?? '',
			dateOfBirth,
			ageConfirmed: action.payload.ageConfirmed !== false,
			nickname: action.payload.nickname ?? '',
			remarks: action.payload.remarks ?? '',
		};
		// An explicit code wins; otherwise the one captured from the invite link the player opened.
		const invitedBy: string | null = action.payload.invitedBy || (yield call(getPendingInviteCode));
		if (invitedBy) {
			registrationBodyObject.invitedBy = invitedBy;
		}

		const registrationBodyJson = JSON.stringify(registrationBodyObject);

		const { Error } = yield call(
			[API, API.authRequest],
			'/registration',
			'post',
			registrationBodyJson,
			{
				'Content-Type': 'application/json',
				...API.getXCasinoHeader(),
			},
		);

		if (Error) {
			const errorData = Error?.response?.data || {};
			const ban = toBanNotice(errorData, 'Registration failed');
			yield put(registerFailure(errorData));
			if (ban) {
				yield put(setBanNotice(ban));
			}
			return;
		}

		yield call(clearPendingInviteCode);
		yield put(registerSuccess());

		yield put(
			loginRequest({
				username,
				password,
			}),
		);
	} catch (e: any) {
		yield put(registerFailure(e?.message || 'Registration failed'));
	}
}

function* refreshAccessTokenSaga() {
	const storedRefreshToken: string | null = yield call([LS, LS.getAsync], 'refresh_token');

	if (!storedRefreshToken) {
		throw new Error('No refresh token found');
	}

	const data: {
		access_token: string;
		refresh_token?: string;
		id?: number;
	} = yield call(refreshTokenRequest, storedRefreshToken);

	const newAccessToken = data?.access_token;
	const newRefreshToken = data?.refresh_token;

	if (!newAccessToken) {
		throw new Error('No access token received from refresh endpoint');
	}

	LS.set('access_token', newAccessToken);

	if (newRefreshToken) {
		LS.set('refresh_token', newRefreshToken);
	}

	const storedUser = LS.get('auth_user');
	if (storedUser) {
		try {
			const parsed = JSON.parse(storedUser);

			const updatedUser = {
				...parsed,
				accessToken: newAccessToken,
				refreshToken: newRefreshToken ?? parsed.refreshToken,
			};

			LS.set('auth_user', JSON.stringify(updatedUser));
		} catch {
			// ignore parse errors
		}
	}

	return newAccessToken;
}

function* checkAuthWorker() {
	try {
		const token: string | null = yield call([LS, LS.getAsync], 'access_token');

		if (!token) {
			yield put(checkAuthFailure());
			yield put(logout());
			return;
		}

		// Show the cached profile (bonus cooldown, chips, level) while the real one is
		// in flight instead of rendering placeholders for the whole round trip.
		const cachedUser: UserProfile | undefined = yield call(loadStoredUser);
		if (cachedUser) {
			yield put(rehydrateAuth({ user: cachedUser }));
		}

		if (isTokenExpired(token)) {
			try {
				const newAccessToken: string = yield call(refreshAccessTokenSaga);
				yield put(checkAuthSuccess({ token: newAccessToken }));
				yield put(profileRequest());
				return;
			} catch {
				analyticsEvents.trackUserLogout();
				yield call([cleverTapService, 'trackEvent'], 'User Logout', {
					reason: 'refresh_failed',
					platform: 'web',
				});
				deltaDNAService.reset();

				yield put(checkAuthFailure());
				yield put(logout());
				return;
			}
		}

		yield put(checkAuthSuccess({ token }));
		yield put(profileRequest());
	} catch {
		yield put(checkAuthFailure());
		yield put(logout());
	}
}

function* fetchUserStats(action: ReturnType<typeof userStatsRequest>) {
	try {
		const raw = yield call(getUserStatistics, action.payload);
		const stats: UserStatistics = mapStatistics(raw);
		yield put(userStatsSuccess(stats));
	} catch (e: any) {
		yield put(userStatsFailure(e?.message || 'Failed to load user statistics'));
	}
}

function* afterLoginSaga() {
	try {
		console.log('[Auth] afterLoginSaga START');
		const profileRaw = yield call(getUserProfile);
		console.log('[Auth] profileRaw:', profileRaw ? 'OK' : 'EMPTY', profileRaw);
		if (!profileRaw) {
			yield put(profileFailure('No profile data received'));
			return;
		}

		const user: UserProfile = mapProfile(profileRaw);

		const accessToken = LS.get('access_token') || undefined;
		const refreshToken = LS.get('refresh_token') || undefined;

		const enrichedUser: UserProfile = {
			...user,
			...(accessToken ? { accessToken } : {}),
			...(refreshToken ? { refreshToken } : {}),
		};

		LS.set('auth_user', JSON.stringify(enrichedUser));
		yield put(profileSuccess(enrichedUser));

		yield put(miniGamesRewardsRequest());

		analyticsEvents.trackUserLogin();
		yield call([cleverTapService, 'setUserProfile'], {
			Identity: user.id || user.numericId.toString(),
			Name: user.username,
			Email: user.email,
			player_level: user.level || 1,
			is_vip: user.isVip || false,
			total_chips: user.chips || 0,
			avatar: user.avatarFileId || '',
			is_sound_enabled: user.isSoundEffectsEnabled || false,
			is_hand_power_enabled: user.isHandPowerEnabled || false,
		});

		yield call([cleverTapService, 'trackEvent'], 'User Login', {
			method: 'email',
			platform: 'web',
			user_id: user.id,
			username: user.username,
			is_vip: user.isVip,
			level: user.level,
		});
		yield call([deltaDNAService, 'initialize'], user.id || user.numericId.toString());
		console.log('[Auth] ✅ User profile and analytics tracking complete');

		console.log('[Auth] mapped user', { id: user.id, username: user.username });
		console.log('[Auth] profileSuccess.type', profileSuccess.type);
		yield put(userStatsRequest());

		yield call([firebaseMessaging, 'syncTokenToBackend']);
	} catch (err: any) {
		console.log('[Auth] afterLoginSaga ERROR', err?.message, err);
		yield put(profileFailure(err.message || 'Profile failed'));
		yield put(userStatsFailure(err?.message || 'Failed to load user statistics'));
	}
}

function* refetchProfileForSocialAvatar() {
	const RETRY_DELAYS_MS = [3000, 5000];

	for (const retryDelay of RETRY_DELAYS_MS) {
		yield delay(retryDelay);

		const avatarFileId: string | null | undefined = yield select(
			(s: RootState) => s.auth.user?.avatarFileId,
		);
		if (avatarFileId) {
			return;
		}

		yield put(profileRequest());
	}
}

function* profileWorker() {
	try {
		const profileRaw = yield call(getUserProfile);
		if (!profileRaw) {
			yield put(profileFailure('No profile data received'));
			return;
		}

		const user: UserProfile = mapProfile(profileRaw);

		let existingTokens: Pick<UserProfile, 'accessToken' | 'refreshToken'> = {};
		const stored = LS.get('auth_user');
		if (stored) {
			try {
				const parsed = JSON.parse(stored) as UserProfile;
				existingTokens = {
					accessToken: parsed.accessToken,
					refreshToken: parsed.refreshToken,
				};
			} catch {
				existingTokens = {};
			}
		}

		const enrichedUser: UserProfile = {
			...user,
			...existingTokens,
		};

		LS.set('auth_user', JSON.stringify(enrichedUser));
		yield put(profileSuccess(enrichedUser));
	} catch (err: any) {
		const status = err?.response?.status;
		if (status === 401 || status === 403) {
			yield put(logout());
			return;
		}
		yield put(profileFailure(err.message || 'Profile failed'));
	}
}

function* persistBonusCooldownWorker() {
	try {
		const user: UserProfile | undefined = yield select((s: RootState) => s.auth.user);
		if (!user) return;

		LS.set('auth_user', JSON.stringify(user));
	} catch (e) {
		console.error('Failed to save updated bonus cooldown to localStorage', e);
	}
}

function* updateProfileWorker(action: ReturnType<typeof updateProfileRequest>) {
	try {
		const result: UpdateProfileResult = yield call(updateProfile, action.payload);
		// e.g. a new avatar the server holds for review instead of applying straight away.
		if (result?.notice) toast.info('profile.update_notice', 'Profile', result.notice);

		const requestedUsername = (action.payload as { username?: string })?.username;
		const changedAvatar = !!(action.payload as { avatarFileId?: string })?.avatarFileId;

		const currentUser: UserProfile | undefined = yield select((s: RootState) => s.auth.user);
		// The rename just used one of the free changes; until the read model catches up, count it here.
		const changesLeftAfterRename =
			currentUser?.usernameChangesLeft == null
				? currentUser?.usernameChangesLeft
				: Math.max(0, currentUser.usernameChangesLeft - 1);
		// Likewise a new avatar, even one held for review.
		const avatarChangesLeftAfter =
			!changedAvatar || currentUser?.avatarChangesLeft == null
				? currentUser?.avatarChangesLeft
				: Math.max(0, currentUser.avatarChangesLeft - 1);
		if (currentUser && requestedUsername) {
			const optimisticUser: UserProfile = {
				...currentUser,
				username: requestedUsername,
				usernameChangesLeft: changesLeftAfterRename,
			};
			LS.set('auth_user', JSON.stringify(optimisticUser));
			yield put(profileSuccess(optimisticUser));
		}

		let user: UserProfile | undefined;
		for (let attempt = 0; attempt < 4; attempt++) {
			if (attempt > 0) yield delay(600);
			const finalProfile: RawProfileData = yield call(getUserProfile);
			user = mapProfile(finalProfile);
			if (!requestedUsername || user.username === requestedUsername) break;
		}

		if (!user) {
			throw new Error('No profile data received');
		}

		if (requestedUsername && user.username !== requestedUsername) {
			user = { ...user, username: requestedUsername, usernameChangesLeft: changesLeftAfterRename };
		}
		if (
			changedAvatar &&
			user.avatarChangesLeft != null &&
			avatarChangesLeftAfter != null &&
			user.avatarChangesLeft > avatarChangesLeftAfter
		) {
			user = { ...user, avatarChangesLeft: avatarChangesLeftAfter };
		}

		let existingTokens: Pick<UserProfile, 'accessToken' | 'refreshToken'> = {};
		const stored = LS.get('auth_user');
		if (stored) {
			try {
				const parsed = JSON.parse(stored) as UserProfile;
				existingTokens = {
					accessToken: parsed.accessToken,
					refreshToken: parsed.refreshToken,
				};
			} catch {
				existingTokens = {};
			}
		}

		const enrichedUser: UserProfile = {
			...user,
			...existingTokens,
		};

		LS.set('auth_user', JSON.stringify(enrichedUser));
		yield put(profileSuccess(enrichedUser));

		yield put(updateProfileSuccess());
	} catch (e: any) {
		const msg = getApiErrorMessage(e, 'Update profile failed');
		yield put(updateProfileFailure(msg));
		toast.errorText(msg);
	}
}

function* reportUserWorker(action: ReturnType<typeof reportUserRequest>) {
	try {
		const reporter = yield select((state: RootState) => state.auth.user);

		if (!reporter) throw new Error('You must be logged in to report a player');

		const body = {
			reporterId: reporter.id,
			reportedId: action.payload.reportedId,
			reportType: action.payload.reportType,
			reason: action.payload.reason || '',
			gameContext: action.payload.gameContext || 'PROFILE',
		};

		yield call(createPlayerReport, body);

		yield put(reportUserSuccess());
		toast.success(
			'report.success_title',
			'Report sent',
			'Thank you - the team will review this player.',
		);
	} catch (e: any) {
		const msg = e?.response?.data?.message || e.message || 'Report failed';
		yield put(reportUserFailure(msg));
		toast.error('report.failed_title', 'Report failed', msg);
	}
}

function* googleLoginSaga(action: ReturnType<typeof googleLoginRequest>) {
	let idToken = action.payload?.idToken;

	try {
		console.log('🔵 [Google Login Saga] Starting Google authentication');
		console.log('🔵 [Google Login Saga] googleSignIn type:', typeof googleSignIn);

		if (typeof googleSignIn !== 'function') {
			console.error('❌ [Google Login Saga] googleSignIn is not a function');
			throw new Error('Google Sign-In is not a function');
		}

		console.log('🔵 [Google Login Saga] Calling googleSignIn()...');
		idToken = idToken || (yield call(googleSignIn));
		console.log(
			'🔵 [Google Login Saga] Received idToken:',
			idToken ? `${idToken.substring(0, 20)}...` : 'null/undefined',
		);

		if (!idToken) {
			console.error('❌ [Google Login Saga] No Google idToken received');
			throw new Error('No Google idToken received');
		}

		console.log('🔵 [Google Login Saga] Calling socialLoginGoogle API...');
		const data: any = yield call(socialLoginGoogle, idToken, action.payload?.confirmed);
		console.log(
			'🔵 [Google Login Saga] API response:',
			data ? { hasAccessToken: !!data.access_token, hasUser: !!data.user } : 'null',
		);

		const accessToken = data?.access_token || data?.accessToken || data?.token;
		const refreshToken = data?.refresh_token || data?.refreshToken || data?.refresh;
		console.log(
			'🔵 [Google Login Saga] Extracted accessToken:',
			accessToken ? `${accessToken.substring(0, 20)}...` : 'null/undefined',
		);

		if (!accessToken) {
			console.error('❌ [Google Login Saga] No access token received from backend');
			console.error('❌ [Google Login Saga] Full response:', JSON.stringify(data, null, 2));
			throw new Error('No access token received from backend');
		}

		console.log('🔵 [Google Login Saga] Saving access token to localStorage...');
		LS.set('access_token', accessToken);
		if (refreshToken) {
			LS.set('refresh_token', refreshToken);
		}

		console.log('🔵 [Google Login Saga] Dispatching loginSuccess...');

		yield put(loginSuccess({ token: accessToken, user: data?.user }));
		yield spawn(refetchProfileForSocialAvatar);

		console.log('✅ [Google Login Saga] Login successful!');
		toast.success('auth.login_success_title', 'Login successful', 'Welcome back!');
	} catch (err: any) {
		const errorData = err?.response?.data || {};
		const code = errorData?.code || '';
		const msg = getAuthErrorMessage(errorData, err?.message || 'Google login failed');
		console.error('❌ [Google Login Saga] Login failed');
		console.error('❌ [Google Login Saga] Error message:', msg);
		console.error('❌ [Google Login Saga] Error object:', err);
		console.error('❌ [Google Login Saga] Error stack:', err?.stack);
		console.error(
			'❌ [Google Login Saga] Full error:',
			JSON.stringify(err, Object.getOwnPropertyNames(err), 2),
		);

		const ban = toBanNotice(errorData, msg);
		if (ban) {
			yield put(googleLoginFailure(msg));
			yield put(setBanNotice(ban));
			return;
		}

		if (
			code === 'T_AUTH_LOGIN_ACTIVE_SESSION_CONFIRM_REQUIRED' &&
			action.payload?.confirmed !== true &&
			idToken
		) {
			yield put(
				setLoginPendingConfirmation({
					type: 'google',
					idToken,
				}),
			);
			return;
		}

		yield put(googleLoginFailure(msg));
		toast.error('auth.login_failed_title', 'Google login failed', msg);
	}
}

function* appleLoginSaga(action: ReturnType<typeof appleLoginRequest>) {
	const traceId = `apple_${Date.now()}`;
	let idToken = action.payload?.idToken;

	try {
		console.log(`🍎 [Apple Login Saga][${traceId}] Starting Apple authentication`);
		console.log(`🍎 [Apple Login Saga][${traceId}] Calling appleSignIn()...`);
		idToken = idToken || (yield call(appleSignIn));
		console.log(
			`🍎 [Apple Login Saga][${traceId}] Received idToken:`,
			idToken ? `${idToken.substring(0, 20)}...` : 'null/undefined',
		);

		if (!idToken) {
			console.error(`🔴 [Apple Login Saga][${traceId}] No Apple identity token received`);
			throw new Error('No Apple identity token received');
		}

		console.log(`🍎 [Apple Login Saga][${traceId}] Calling socialLoginApple API...`);
		const data: any = yield call(socialLoginApple, idToken, action.payload?.confirmed);
		console.log(
			`🍎 [Apple Login Saga][${traceId}] API response:`,
			data ? { hasAccessToken: !!data.access_token, hasUser: !!data.user } : 'null',
		);

		const accessToken = data?.access_token || data?.accessToken || data?.token;
		const refreshToken = data?.refresh_token || data?.refreshToken || data?.refresh;
		console.log(
			`🍎 [Apple Login Saga][${traceId}] Extracted accessToken:`,
			accessToken ? `${accessToken.substring(0, 20)}...` : 'null/undefined',
		);

		if (!accessToken) {
			console.error(`🔴 [Apple Login Saga][${traceId}] No access token received from backend`);
			console.error(
				`🔴 [Apple Login Saga][${traceId}] Full response:`,
				JSON.stringify(data, null, 2),
			);
			throw new Error('No access token received from backend');
		}

		console.log(`🍎 [Apple Login Saga][${traceId}] Saving access token to localStorage...`);
		LS.set('access_token', accessToken);
		if (refreshToken) {
			LS.set('refresh_token', refreshToken);
		}

		console.log(`🍎 [Apple Login Saga][${traceId}] Dispatching loginSuccess...`);
		yield put(loginSuccess({ token: accessToken, user: data?.user }));

		console.log(`✅ [Apple Login Saga][${traceId}] Login successful!`);
		toast.success('auth.login_success_title', 'Login successful', 'Welcome back!');
	} catch (err: any) {
		const errorData = err?.response?.data || {};
		const code = errorData?.code || '';
		const msg = getAuthErrorMessage(errorData, err?.message || 'Apple login failed');
		console.error(`🔴 [Apple Login Saga][${traceId}] Login failed`);
		console.error(`🔴 [Apple Login Saga][${traceId}] Error message:`, msg);
		console.error(`🔴 [Apple Login Saga][${traceId}] Error object:`, err);
		console.error(`🔴 [Apple Login Saga][${traceId}] Error stack:`, err?.stack);
		console.error(
			`🔴 [Apple Login Saga][${traceId}] Full error:`,
			JSON.stringify(err, Object.getOwnPropertyNames(err), 2),
		);

		const ban = toBanNotice(errorData, msg);
		if (ban) {
			yield put(appleLoginFailure(msg));
			yield put(setBanNotice(ban));
			return;
		}

		if (
			code === 'T_AUTH_LOGIN_ACTIVE_SESSION_CONFIRM_REQUIRED' &&
			action.payload?.confirmed !== true &&
			idToken
		) {
			yield put(
				setLoginPendingConfirmation({
					type: 'apple',
					idToken,
				}),
			);
			return;
		}

		yield put(appleLoginFailure(msg));
		toast.error('auth.login_failed_title', 'Apple login failed', msg);
	}
}

function* facebookLoginSaga(action: ReturnType<typeof facebookLoginRequest>) {
	const traceId = `fb_${Date.now()}`;
	let fbAccessToken = action.payload?.idToken;

	try {
		fbAccessToken = fbAccessToken || (yield call(facebookSignIn));

		if (!fbAccessToken) {
			throw new Error('No Facebook access token received');
		}

		const data: any = yield call(socialLoginFacebook, fbAccessToken, action.payload?.confirmed);

		console.log(`🟢 [FB][${traceId}] backend response`, {
			hasData: !!data,
			keys: data ? Object.keys(data) : null,
			raw: data,
		});

		const accessToken = data?.access_token || data?.accessToken || data?.token;
		const refreshToken = data?.refresh_token || data?.refreshToken || data?.refresh;

		if (!accessToken) {
			throw new Error('No access token received from backend');
		}

		LS.set('access_token', accessToken);
		if (refreshToken) {
			LS.set('refresh_token', refreshToken);
		}

		yield put(loginSuccess({ token: accessToken, user: data?.user }));
		yield spawn(refetchProfileForSocialAvatar);

		toast.success('auth.login_success_title', 'Login successful', 'Welcome back!');
	} catch (err: any) {
		console.error('❌ [FB Login] Full error:', {
			status: err?.response?.status,
			statusText: err?.response?.statusText,
			data: JSON.stringify(err?.response?.data),
			headers: err?.response?.headers,
			message: err?.message,
		});
		const errorData = err?.response?.data || {};
		const code = errorData?.code || '';
		const msg = getAuthErrorMessage(errorData, err?.message || 'Facebook login failed');

		const ban = toBanNotice(errorData, msg);
		if (ban) {
			yield put(facebookLoginFailure(msg));
			yield put(setBanNotice(ban));
			return;
		}

		if (
			code === 'T_AUTH_LOGIN_ACTIVE_SESSION_CONFIRM_REQUIRED' &&
			action.payload?.confirmed !== true &&
			fbAccessToken
		) {
			yield put(
				setLoginPendingConfirmation({
					type: 'facebook',
					idToken: fbAccessToken,
				}),
			);
			return;
		}

		yield put(facebookLoginFailure(msg));

		toast.error('auth.login_failed_title', 'Facebook login failed', msg);
	}
}

function* logoutCloseSocketWorker() {
	try {
		yield call(clearOnlinePresence);
	} catch {
		// Presence expiry remains the fallback if this best-effort request fails.
	}
	try {
		const refreshToken: string | null = yield call([LS, LS.getAsync], 'refresh_token');
		if (refreshToken) {
			yield call(logoutRequest, refreshToken);
		}
	} catch {
		// Logout API call is best-effort; proceed with local cleanup
	}
	yield call(clearAuthFromStorage);
	yield call(closeSocket);
}

export function* authRootSaga() {
	yield takeLatest(logout.type, logoutCloseSocketWorker);
	yield takeLatest(loginRequest.type, loginSaga);
	yield takeLatest(registerRequest.type, registerWorker);
	yield takeLatest(checkAuthRequest.type, checkAuthWorker);
	yield takeLatest(loginSuccess.type, afterLoginSaga);
	yield takeLatest(userStatsRequest, fetchUserStats);
	yield takeLatest(profileRequest.type, profileWorker);
	yield takeLatest(checkAuthSuccess.type, function* () {
		yield put(userStatsRequest());
		yield call([firebaseMessaging, 'syncTokenToBackend']);
	});

	yield takeLatest(updateBonusCooldown.type, persistBonusCooldownWorker);
	yield takeLatest(updateProfileRequest.type, updateProfileWorker);
	yield takeLatest(reportUserRequest.type, reportUserWorker);
	yield takeLatest(appleLoginRequest.type, appleLoginSaga);
	yield takeLatest(facebookLoginRequest.type, facebookLoginSaga);
	yield takeLatest(googleLoginRequest.type, googleLoginSaga);
}

function mapStatistics(d: any): UserStatistics {
	const player = d?.data?.player ?? {};
	const vipExpiredAt = player.vip_expired_at ?? player.vipExpiredAt ?? null;

	return {
		player: {
			avatar: player.avatar ?? {},
			username: player.username ?? '',
			registrationDate: player.registrationDate,
			lastLoginDate: player.lastLoginDate,
			vip_expired_at: vipExpiredAt,
			gender: player.gender ?? null,
			age: player.age ?? null,
			location: player.location ?? null,
			description: player.description ?? null,
		},
		level: {
			current: Number(d?.data.level?.current ?? 0),
			name: String(d?.data.level?.name ?? ''),
		},
		gamesData: {
			svarka_wins: Number(d?.data.gamesData?.svarka_wins ?? 0),
			svarka_loses: Number(d?.data.gamesData?.svarka_loses ?? 0),
			svarka_chips_wins: String(d?.data.gamesData?.svarka_chips_wins ?? '0'),
			svarka_experience: Number(d?.data.gamesData?.svarka_experience ?? 0),
			svarka_time_played: Number(d?.data.gamesData?.svarka_time_played ?? 0),
			svarka_last_time_played: d?.data.gamesData?.svarka_last_time_played ?? null,
			svarka_level: Number(d?.data.gamesData?.svarka_level ?? 0),
			svarka_minimal_experience: Number(d?.data.gamesData?.svarka_minimal_experience ?? 0),
			svarka_maximal_experience: Number(d?.data.gamesData?.svarka_maximal_experience ?? 0),
		},
		statistics: {
			svarka_time_played: String(d?.data.statistics?.timePlayed ?? '0'),
			winLoseRatio: Number(d?.data.statistics?.winLoseRatio ?? 0),
			totalChipsGained: d?.data.statistics?.totalChipsGained ?? 0,
		},
		wallet: {
			chips: d?.data.wallet?.chips == null ? null : Number(d.data.wallet.chips),
		},
	};
}
